import React from "react";
import type { ICellRendererParams } from "ag-grid-community";
import styles from "./ClientDocumentsPage.module.css";
import { CygnusTooltip } from "reusable-components/CygnusTooltip/CygnusTooltip";

export type StatusClass =
  | "completed"
  | "review"
  | "draft"
  | "processing"
  | "error"
  | "action-pending"
  | "imported"
  | "not-imported"
  | "partial"
  | "importing"
  | "other";

const stripBullet = (s: string) => s.replace(/^[\s•·\-–]+/, "").trim();

export const categorizeStatus = (raw: string | undefined | null): StatusClass => {
  const k = stripBullet(String(raw ?? "")).toLowerCase();
  if (!k) return "other";
  const countMatch = k.match(/^(\d+)\/(\d+)\s+imported$/);
  if (countMatch) {
    const done = parseInt(countMatch[1], 10);
    const total = parseInt(countMatch[2], 10);
    return total > 0 && done === total ? "imported" : "partial";
  }
  if (["imported", "return selected"].includes(k)) return "imported";
  if (["not imported", "not_imported"].includes(k) || /^select(\s+a)?\s+return$/i.test(k)) return "not-imported";
  if (["completed","complete","verified","approved","comp"].includes(k)) return "completed";
  if (["ready for review","ready_for_review","needs review","awaiting review","awaiting approval","ready"].includes(k)) return "review";
  if (["draft","unverified","incomplete","created"].includes(k)) return "draft";
  if (["processing","in progress","in-progress","queued","running","uploading","extracting","pending"].includes(k)) return "processing";
  if (k.includes("pending upload") || k.includes("awaiting") || k.includes("needs")) return "action-pending";
  if (/\b(error|failed|failure|reject|rejected)\b/i.test(k)) return "error";
  return "other";
};

export const categorizeTaxStatus = (raw: string | undefined | null): StatusClass => {
  const k = String(raw ?? "").trim();
  if (!k || /^not\s*moved\b/i.test(k)) return "not-imported";
  if (/^completed?$/i.test(k)) return "imported";
  if (/\bstep\b\s*[:\-]?\s*\d+\s*(?:\/|of)\s*\d+\b/i.test(k)) return "importing";
  if (/\b(error|failed|failure|reject|rejected)\b/i.test(k)) return "error";
  if (/^pending upload?$/i.test(k)) return "action-pending";
  return "other";
};

const parseStep = (s: string): { current: number; total: number; pct: number } | null => {
  const m = s.match(/step\s*[:\-]?\s*(\d+)\s*(?:\/|of)\s*(\d+)/i);
  if (!m) return null;
  const current = parseInt(m[1], 10);
  const total = parseInt(m[2], 10);
  if (total === 0) return { current: 0, total: 0, pct: 0 };
  return { current, total, pct: Math.round((current / total) * 100) };
};

const truncate = (v: unknown, n = 280) => {
  const s = typeof v === "string" ? v : v == null ? "" : JSON.stringify(v);
  const t = s.trim();
  return t.length > n ? t.slice(0, n - 1) + "…" : t;
};

type FieldKey = "status" | "taxstatus" | undefined;
const getFieldFromParams = (p: ICellRendererParams<any, any>): FieldKey => {
  const name = String(p.colDef?.field ?? p.colDef?.colId ?? "")
    .toLowerCase()
    .replace(/\s+/g, "");
  if (name === "taxstatus") return "taxstatus";
  if (name === "status") return "status";
  return undefined;
};

const DOT_TEXT_CONFIG: Record<string, { dot: string; text: string }> = {
  "imported":     { dot: "#16a34a", text: "#15803d" },
  "not-imported": { dot: "#9ca3af", text: "#6b7280" },
  "error":        { dot: "#dc2626", text: "#b91c1c" },
  "partial":      { dot: "#f59e0b", text: "#d97706" },
};

export const StatusCellRenderer = (p: ICellRendererParams<any, any>) => {
  const field = getFieldFromParams(p);
  const raw =
    (typeof p.value === "string" ? p.value : undefined) ??
    (field ? String(p.data?.[field === "status" ? "Status" : "TaxStatus"] ?? "") : "");

  if (!raw.trim()) return null;

  const cls = field === "taxstatus" ? categorizeTaxStatus(raw) : categorizeStatus(raw);

  if (cls in DOT_TEXT_CONFIG) {
    const { dot, text } = DOT_TEXT_CONFIG[cls];
    const isCountFormat = /^\d+\/\d+\s+imported$/i.test(raw.trim());
    const displayText =
      cls === "partial"      ? raw                          :
      cls === "imported"     ? (isCountFormat ? raw : "Imported") :
      cls === "not-imported" ? "Not Imported"               :
      cls === "error"        ? "Import Failed"              : raw;

    const dotSpan = (
      <span className={styles["client-documents-dotIndicator"]}>
        <span className={styles["client-documents-dotIndicatorDot"]} style={{ background: dot }} />
        <span className={styles["client-documents-dotIndicatorText"]} style={{ color: text }}>
          {displayText}
        </span>
      </span>
    );

    if (cls !== "error") return dotSpan;

    const taxMessage = p.data?.TaxMessage;
    const hasTaxMessage =
      taxMessage != null && taxMessage !== 0 && taxMessage !== "" && taxMessage !== "0";
    const tipText = hasTaxMessage
      ? truncate(typeof taxMessage === "string" ? taxMessage : JSON.stringify(taxMessage), 280)
      : "Import failed.";

    return (
      <CygnusTooltip text={tipText}>
        {dotSpan}
      </CygnusTooltip>
    );
  }

  if (cls === "importing") {
    const step = parseStep(raw);
    const pct = step?.pct ?? 0;

    return (
      <CygnusTooltip text="This should take ~1min to complete">
        <div className={styles["client-documents-importingWrapper"]}>
          <span className={styles["client-documents-importingLabelRow"]}>
            <span className={styles["client-documents-dotIndicator"]}>
              <span
                className={styles["client-documents-dotIndicatorDot"]}
                style={{ background: "#6b7280" }}
              />
              <span
                className={styles["client-documents-dotIndicatorText"]}
                style={{ color: "#374151" }}
              >
                Importing...
              </span>
            </span>
            <span
              className={styles["client-documents-dotIndicatorText"]}
              style={{ color: "#374151" }}
            >
              {pct}%
            </span>
          </span>
          <div className={styles["client-documents-progressTrack"]}>
            <div
              className={styles["client-documents-progressFill"]}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </CygnusTooltip>
    );
  }

  const isSelectReturn = /^select(\s+a)?\s+return$/i.test(String(raw || ""));
  const isPendingUpload = /\bpending\s+upload\b/i.test(String(raw || ""));

  const ctx: any = (p as any).context || {};
  const openUpload =
    (p.colDef as any)?.cellRendererParams?.onPendingUpload ??
    ctx.onPendingUpload ??
    ctx.openUploadDialog;

  const pill = (
    <span
      className={[
        styles["client-documents-statusPill"],
        styles["client-documents-status--" + cls],
        (isSelectReturn || isPendingUpload) ? styles["client-documents-status--clickable"] : "",
      ].join(" ")}
      data-status-class={cls}
      data-status-field={field}
      tabIndex={(isSelectReturn || isPendingUpload) ? 0 : -1}
      onClick={() => {
        if (isPendingUpload && typeof openUpload === "function") openUpload(p.data);
      }}
      onKeyDown={(e) => {
        if (isPendingUpload && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          if (typeof openUpload === "function") openUpload(p.data);
        }
      }}
      style={{ cursor: (isSelectReturn || isPendingUpload) ? "pointer" : "default" }}
      role={(isSelectReturn || isPendingUpload) ? "button" : undefined}
    >
      {raw || "—"}
    </span>
  );

  if (!isSelectReturn) return pill;

  return (
    <CygnusTooltip text="Please select a Return Id by clicking ⬇️ in actions">
      {pill}
    </CygnusTooltip>
  );
};
